/**
 * Zustand store for milestone artifacts — unlocked set + reveal queue.
 *
 * Persists `unlocked` to localStorage via the `persist` middleware. The
 * reveal queue is in-memory only (a reload mid-celebration just shows
 * the artifact in its layer; the celebration was already had).
 *
 * Multi-unlock policy (JB 2026-05-24):
 *   • Up to 3 reveals shown in order one by one
 *   • The 4th and beyond get coalesced into a single summary entry
 *     `{ kind: 'summary', count: N, ids: [...] }`
 *   • The summary entry counts as one reveal, never more than 3 in a
 *     row at the user.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ARTIFACTS_BY_ID, VISUAL_ARTIFACTS_CATALOG } from './catalog.js';
import { emit } from '../telemetry/emit.js';
import type { VisualArtifact } from './types.js';

/** What sits in the reveal queue. */
export type RevealEntry =
  | { readonly kind: 'single'; readonly artifact: VisualArtifact }
  | {
      readonly kind: 'summary';
      readonly artifacts: readonly VisualArtifact[];
    };

interface ArtifactsStore {
  /** Set of unlocked artifact ids, persisted across reloads. */
  readonly unlocked: ReadonlySet<string>;
  /** FIFO of pending reveals — overlay reads the head. */
  readonly revealQueue: readonly RevealEntry[];

  /**
   * Push a batch of newly unlocked artifacts. Adds them to `unlocked`
   * immediately (visible in their layers right away) AND queues them
   * for the reveal overlay according to the multi-unlock policy.
   */
  recordNewlyUnlocked(artifacts: readonly VisualArtifact[]): void;

  /** Advance past the head of the queue — called by overlay on dismiss. */
  dismissCurrentReveal(): void;

  /** Test-only: wipe everything. */
  _resetForTests(): void;
}

const MAX_INDIVIDUAL_REVEALS = 3;

export const useArtifactsStore = create<ArtifactsStore>()(
  persist(
    (set, get) => ({
      unlocked: new Set<string>(),
      revealQueue: [],

      recordNewlyUnlocked(artifacts) {
        if (artifacts.length === 0) return;

        // Add to unlocked set (the artifact becomes visible immediately).
        const nextUnlocked = new Set(get().unlocked);
        for (const a of artifacts) {
          nextUnlocked.add(a.id);
          emit('artifact.unlocked', {
            artifact_id: a.id,
            trigger_kind: a.trigger.kind,
            epoch: 0, // we don't have current epoch in scope here; left at 0 — caller can enrich
          });
        }

        // Build queue entries: up to N individual, rest in a summary.
        const additions: RevealEntry[] = [];
        if (artifacts.length <= MAX_INDIVIDUAL_REVEALS) {
          for (const a of artifacts) {
            additions.push({ kind: 'single', artifact: a });
          }
        } else {
          for (let i = 0; i < MAX_INDIVIDUAL_REVEALS - 1; i++) {
            const a = artifacts[i];
            if (a) additions.push({ kind: 'single', artifact: a });
          }
          additions.push({
            kind: 'summary',
            artifacts: artifacts.slice(MAX_INDIVIDUAL_REVEALS - 1),
          });
        }

        set({
          unlocked: nextUnlocked,
          revealQueue: [...get().revealQueue, ...additions],
        });
      },

      dismissCurrentReveal() {
        const q = get().revealQueue;
        if (q.length === 0) return;
        const [head, ...rest] = q;
        if (head?.kind === 'single') {
          emit('artifact.reveal_dismissed', {
            artifact_id: head.artifact.id,
            duration_ms: 0, // overlay can enrich with actual visible time
          });
        }
        set({ revealQueue: rest });
      },

      _resetForTests() {
        set({ unlocked: new Set(), revealQueue: [] });
      },
    }),
    {
      name: 'sai-artifacts-v1',
      // `unlocked` is a Set — zustand's persist needs help serializing.
      partialize: (s) => ({
        unlocked: Array.from(s.unlocked),
      }),
      // On hydrate, rebuild the Set from the array.
      merge: (persisted, current) => {
        const p = persisted as { unlocked?: string[] } | undefined;
        const rawList = p?.unlocked ?? [];
        // Filter to only known ids — protects against stale entries from
        // an old build where an artifact id was renamed or removed.
        const validIds = rawList.filter((id) => ARTIFACTS_BY_ID.has(id));
        return {
          ...current,
          unlocked: new Set<string>(validIds),
        };
      },
    },
  ),
);

/** Convenience selector — is an artifact currently unlocked? */
export function isArtifactUnlocked(id: string): boolean {
  return useArtifactsStore.getState().unlocked.has(id);
}

/** Convenience — all unlocked artifacts as full objects (filtered + ordered by catalog). */
export function getUnlockedArtifacts(): readonly VisualArtifact[] {
  const unlocked = useArtifactsStore.getState().unlocked;
  return VISUAL_ARTIFACTS_CATALOG.filter((a) => unlocked.has(a.id));
}
